﻿import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AdminButton, AdminErrorBanner, AdminLoadingState, AdminPageLayout } from "@/components/admin";
import EmergencySummaryCards from "./components/EmergencySummaryCards";
import EmergencyList from "./components/EmergencyList";
import EmergencyDetailPanel from "./components/EmergencyDetailPanel";
import NotificationLogCard from "./components/NotificationLogCard";
import QuickFilters from "./components/QuickFilters";
import EmergencyInfoCard from "./components/EmergencyInfoCard";
import { fetchAdminEmergencyLifecycle, fetchEmergencyEventDetail, fetchEmergencyEventNotificationLogs, fetchRecentEmergencyEvents, reopenAdminEmergencyLifecycle, type AdminEmergencyLifecycle } from "../../../lib/api";
import type { EmergencyEventDetail, EmergencyEventItem, NotificationLogItem } from "@/types.ts";
const EmergencyPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const seniorFilterId = Number(searchParams.get("seniorUserAccountId"));
    const selectedEventId = Number(searchParams.get("selectedEvent"));
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [eventList, setEventList] = useState<EmergencyEventItem[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<EmergencyEventDetail | null>(null);
    const [notificationLogs, setNotificationLogs] = useState<NotificationLogItem[]>([]);
    const [loadingList, setLoadingList] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [lifecycle, setLifecycle] = useState<AdminEmergencyLifecycle | null>(null);
    const [reasonCode, setReasonCode] = useState("OPERATOR_REQUEST");
    const [reason, setReason] = useState("");
    const [auditMessage, setAuditMessage] = useState("");
    const loadList = useCallback(async () => {
        setLoadingList(true);
        setErrorMessage("");
        try {
            const items = await fetchRecentEmergencyEvents({
                seniorUserAccountId: Number.isFinite(seniorFilterId) && seniorFilterId > 0 ? seniorFilterId : undefined,
            });
            setEventList(items);
            setSelectedId((current) => current && items.some((item) => item.eventId === current) ? current : items[0]?.eventId ?? null);
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "긴급 이벤트 목록을 불러오지 못했습니다.");
        }
        finally {
            setLoadingList(false);
        }
    }, [seniorFilterId]);
    const loadDetail = useCallback(async (eventId: number) => {
        setLoadingDetail(true);
        setErrorMessage("");
        try {
            const [detail, logs, lifecycleDetail] = await Promise.all([
                fetchEmergencyEventDetail(eventId),
                fetchEmergencyEventNotificationLogs(eventId),
                fetchAdminEmergencyLifecycle(eventId),
            ]);
            setSelectedEvent(detail);
            setNotificationLogs(logs);
            setLifecycle(lifecycleDetail);
        }
        catch (error) {
            setSelectedEvent(null);
            setNotificationLogs([]);
            setLifecycle(null);
            setErrorMessage(error instanceof Error ? error.message : "긴급 이벤트 상세를 불러오지 못했습니다.");
        }
        finally {
            setLoadingDetail(false);
        }
    }, []);
    useEffect(() => {
        void loadList();
    }, [loadList]);
    useEffect(() => {
        if (Number.isFinite(selectedEventId) && selectedEventId > 0) {
            setSelectedId(selectedEventId);
        }
    }, [selectedEventId]);
    useEffect(() => {
        if (selectedId == null) {
            setSelectedEvent(null);
            setNotificationLogs([]);
            return;
        }
        void loadDetail(selectedId);
    }, [selectedId, loadDetail]);
    useEffect(() => {
        const state = location.state as {
            fromLifeSignalDetail?: boolean;
            fromSoundDetail?: boolean;
            elderName?: string;
            elderLoginId?: string;
            selectedEvent?: string | number;
            searchDevice?: string;
            searchDeviceUuid?: string;
        } | null;
        if (!state)
            return;
        const queryParts = [state.elderName, state.elderLoginId, state.searchDevice, state.searchDeviceUuid].filter(Boolean);
        if (queryParts.length > 0) {
            setSearchQuery(queryParts.join(" "));
        }
        const stateEventId = Number(state.selectedEvent);
        if (Number.isFinite(stateEventId) && stateEventId > 0) {
            setSelectedId(stateEventId);
        }
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }, [location.pathname, location.search, location.state, navigate]);
    const handleSelect = (id: number) => {
        setSelectedId(id);
    };
    const handleClose = () => {
        setSelectedId(null);
    };
    const handleReopen = async () => {
        if (!selectedId || !lifecycle)
            return;
        setSaving(true);
        setErrorMessage("");
        try {
            const result = await reopenAdminEmergencyLifecycle(selectedId, lifecycle.incident.versionNo, reasonCode, reason);
            setAuditMessage(`감사 #${result.audit.auditLogId} · ${result.audit.beforeStatus} → ${result.audit.afterStatus}`);
            setReason("");
            await Promise.all([loadList(), loadDetail(selectedId)]);
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "긴급 이벤트 상태 변경에 실패했습니다.");
        }
        finally {
            setSaving(false);
        }
    };
    const pageSubtitle = useMemo(() => {
        if (loadingList)
            return "긴급 이벤트를 불러오는 중";
        return Number.isFinite(seniorFilterId) && seniorFilterId > 0
            ? `Senior ID ${seniorFilterId} · ${eventList.length}건 관리`
            : `총 ${eventList.length}건 관리`;
    }, [eventList.length, loadingList, seniorFilterId]);
    return (<AdminPageLayout
            title="긴급 관제"
            subtitle={`${pageSubtitle} · 통합 업무함 연계`}
            hasIssue={eventList.some((event) => event.eventStatus !== "RESOLVED")}
            issueLabel="긴급 확인 필요"
            actions={
              <AdminButton type="button" variant="primary" size="sm" onClick={() => void loadList()} icon="ri-refresh-line">
                새로고침
              </AdminButton>
            }
          >
          <AdminErrorBanner message={errorMessage}/>

          {loadingList ? (<AdminLoadingState message="긴급 이벤트 목록을 불러오는 중입니다."/>) : (<>
              <div className="mb-4">
                <EmergencySummaryCards events={eventList} notificationLogs={notificationLogs}/>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
                <div className="xl:col-span-3">
                  <EmergencyList events={eventList} totalCount={eventList.length} selectedId={selectedId} onSelect={handleSelect} searchQuery={searchQuery} onSearchChange={setSearchQuery} activeFilter={activeFilter} onRefresh={() => void loadList()}/>
                </div>

                <div className="xl:col-span-1 flex flex-col gap-4">
                  <EmergencyDetailPanel event={selectedEvent} lifecycle={lifecycle} loading={loadingDetail} saving={saving} reasonCode={reasonCode} reason={reason} auditMessage={auditMessage} onReasonCodeChange={setReasonCode} onReasonChange={setReason} onClose={handleClose} onReopen={handleReopen}/>

                  <NotificationLogCard logs={notificationLogs}/>

                  <QuickFilters activeFilter={activeFilter} onFilterChange={setActiveFilter}/>

                  <EmergencyInfoCard />
                </div>
              </div>
            </>)}
    </AdminPageLayout>);
};
export default EmergencyPage;
